/**
 * Page-level filter state for the tasks dashboard grid.
 *
 * The widget platform gives each widget its own config cog, but the tasks
 * dashboard needs TWO filters that apply to the whole grid at once — the
 * workspace picker and the My/Team scope toggle. Both live in the dashboard
 * hero and are read by every tasks widget through this context.
 *
 * Outside the provider (the home command center, where the tasks widgets also
 * appear) the context resolves to the personal, all-workspaces view — exactly
 * the semantics a home widget wants. See
 * docs/platform/dashboard-widget-registry.md.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { DashboardViewMode } from '../types';

export interface TasksDashboardFilters {
  /** Undefined = all workspaces. */
  workspaceId: string | undefined;
  /** 'my' = only the current user's tasks; 'all' = the whole team's. */
  scope: 'my' | 'all';
}

const PERSONAL_ALL_WORKSPACES: TasksDashboardFilters = {
  workspaceId: undefined,
  scope: 'my',
};

const TasksDashboardFiltersContext = createContext<TasksDashboardFilters>(PERSONAL_ALL_WORKSPACES);

interface ProviderProps {
  workspaceId: string | undefined;
  viewMode: DashboardViewMode;
  children: ReactNode;
}

export function TasksDashboardFiltersProvider({ workspaceId, viewMode, children }: ProviderProps) {
  const value = useMemo<TasksDashboardFilters>(
    () => ({ workspaceId, scope: viewMode === 'overview' ? 'all' : 'my' }),
    [workspaceId, viewMode]
  );

  return (
    <TasksDashboardFiltersContext.Provider value={value}>
      {children}
    </TasksDashboardFiltersContext.Provider>
  );
}

/** The filters the surrounding dashboard applies to every tasks widget. */
export function useTasksDashboardFilters(): TasksDashboardFilters {
  return useContext(TasksDashboardFiltersContext);
}
